import React, { useState } from 'react';
import { useDetailedOrganizationRegistrations } from '../../hooks/useOrganizations';
import type { TableProps } from 'antd';
import {
  Button,
  Flex,
  message,
  Space,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useTranslation } from 'react-i18next';
import type { DetailedOrganizationRegistration } from '../../Types/interfaces';
import { RegistrationStatus } from '../../Types/interfaces';
import {
  useColumnSearchPropsWithSearchField,
  useFilterIcon,
} from '../../hooks/useColumnSearch';
import { GrDocumentPdf } from 'react-icons/gr';
import { LuDownload } from 'react-icons/lu';
import {
  DownloadCollaborationDocumentsZip,
  GetOrganizationRegistrationByIdPdfUrl,
} from '../../helper/apiHelper';
import { downloadDocuments } from '../../Utils/downloadWithUrl';
import { format } from 'date-fns';
interface DataType {
  key: React.Key;
  organizationName: string;
  organizationRepresentativeName: string;
  organizationCity: string;
  organizationEmail: string;
  registrationStatus: RegistrationStatus;
}

export const AllRegistrationsTable = (): React.ReactElement => {
  const { data: registrations } = useDetailedOrganizationRegistrations();
  const { t } = useTranslation();
  const { getColumnSearchPropsWithSearchField } =
    useColumnSearchPropsWithSearchField();
  const { filterIcon } = useFilterIcon();
  const [, contextHolder] = message.useMessage();
  const [selectedRows, setSelectedRows] = useState<DataType[]>([]);
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
  const { token } = theme.useToken();

  const getRegistrationStatusTranslation = (
    registrationStatus: RegistrationStatus
  ) => {
    switch (registrationStatus) {
      case RegistrationStatus.registered:
        return t('allRegistrationsTableComp.registered');
      case RegistrationStatus.registeringCollaborationDocument:
        return t('allRegistrationsTableComp.registeringCollaborationDocument');
      case RegistrationStatus.SignedByManager:
        return t('allRegistrationsTableComp.signedByManager');
      case RegistrationStatus.SignedByLegalDepartment:
        return t('allRegistrationsTableComp.signedByLegalDepartment');
      case RegistrationStatus.SignedByRector:
        return t('allRegistrationsTableComp.signedByRector');
      default:
        return t('allRegistrationsTableComp.notRegistered');
    }
  };

  const filterForSigningStatus = Array.from(
    new Set(registrations?.map((reg) => reg.registrationStatus))
  )
    .filter(Boolean)
    .map((status) => ({
      text: getRegistrationStatusTranslation(status),
      value: status,
    }));

  const columns: TableColumnsType<DataType> = [
    {
      title: t('allRegistrationsTableComp.orgName'),
      dataIndex: 'organizationName',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('allRegistrationsTableComp.orgNamePlaceholder'),
        'organizationName'
      ),
    },
    {
      title: t('allRegistrationsTableComp.representativeName'),
      dataIndex: 'organizationRepresentativeName',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('allRegistrationsTableComp.representativeNamePlaceholder'),
        'organizationRepresentativeName'
      ),
    },
    {
      title: t('allRegistrationsTableComp.city'),
      dataIndex: 'organizationCity',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('allRegistrationsTableComp.organizationCityPlaceholder'),
        'organizationCity'
      ),
    },
    {
      title: t('allRegistrationsTableComp.email'),
      dataIndex: 'organizationEmail',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('allRegistrationsTableComp.emailPlaceholder'),
        'organizationEmail'
      ),
    },
    {
      title: t('allRegistrationsTableComp.signingStatus'),
      dataIndex: 'registrationStatus',
      align: 'center',
      filters: filterForSigningStatus,
      onFilter: (value, record) =>
        String(record.registrationStatus) === String(value),
      ...filterIcon,
      render: (_, record) => {
        return (
          <Tag color="green">
            {getRegistrationStatusTranslation(record.registrationStatus)}
          </Tag>
        );
      },
    },
    {
      title: t('allRegistrationsTableComp.actions'),
      dataIndex: 'operations',
      align: 'center',
      render: (_, record: DataType) => {
        return (
          <Space size="middle">
            {/* View PDF button */}
            <Tooltip title={t('allRegistrationsTableComp.viewPDF', 'View PDF')}>
              <Button
                type="text"
                icon={<GrDocumentPdf style={{ fontSize: 22 }} />}
                onClick={async () => {
                  const rawUrl = await GetOrganizationRegistrationByIdPdfUrl(
                    String(record.key)
                  );
                  const pdfUrl = `${rawUrl}?status=${getRegistrationStatusTranslation(record.registrationStatus)}`;
                  window.open(pdfUrl, '_blank');
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const data: DataType[] =
    registrations?.map((registration: DetailedOrganizationRegistration) => ({
      key: Number(registration.id),
      organizationName: registration.organizationName,
      organizationRepresentativeName:
        registration.organizationRepresentativeName,
      organizationCity: registration.organizationCity,
      organizationEmail: registration.organizationEmail,
      registrationStatus: registration.registrationStatus,
    })) ?? [];

  // rowSelection object indicates the need for row selection
  const rowSelection: TableProps<DataType>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      setSelectedRowCount(selectedRowKeys.length);
      setSelectedRows(selectedRows);
    },
  };

  const handleDownloadSelected = async () => {
    const registrationIds: number[] = selectedRows.map((item) =>
      Number(item.key)
    );
    const response = await DownloadCollaborationDocumentsZip(registrationIds);

    const currentDate = new Date();
    const formattedDate = format(currentDate, 'dd-MM-yyyy_HH-mm-ss');

    downloadDocuments(response, `acorduri_${formattedDate}`, 'zip');
  };

  return (
    <>
      {contextHolder}

      {selectedRowCount > 0 && (
        <Flex
          justify="space-between"
          align="center"
          style={{
            backgroundColor: token.colorFillSecondary,
            paddingLeft: 12,
          }}
        >
          <Typography.Text style={{ color: token.colorTextLabel, margin: 5 }}>
            {t('legDepComp.selectedRows')}: {selectedRowCount}
          </Typography.Text>
          <Button
            type="primary"
            icon={<LuDownload />}
            onClick={handleDownloadSelected}
            style={{ margin: 5, borderRadius: 5 }}
          >
            {t('legDepComp.downloadSelected')}
          </Button>
        </Flex>
      )}

      <Table<DataType>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        size="small"
        pagination={{
          defaultPageSize: 15,
          position: ['bottomCenter'],
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50', '100', '200'],
        }}
      />
    </>
  );
};
